import React, { useContext, useEffect, useState } from "react";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
    ButtonWrapper,
    HeaderWrapper,
    PartsCostHeaderWrapper,
    PartsCostWrapper,
    PartsHeaderWrapper
} from "./styled.js";

import { ReactComponent as PlusIcon } from "assets/svgs/plus.svg";
import { CostInput, TaxInput } from "components";

import PartRowDroppable from './PartRow';
import { AppContext } from "hocs/withAppContext";
import Media from "react-media";

// a little helper function to help us
// with reordering the result
const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const PartsForm = ({ onChange }) => {
    const { currencySymbol } = useContext(AppContext);
    const [parts, setParts] = useState([]);

    // main unique clients side keys to help react identify
    // which components to render after a change
    const _id = parts.length !== 0
        ? parts[parts.length - 1]._id + 1
        : 0;

    const createPart = (part) => setParts([...parts, part]);
    const updatePart = (part, index) => {
        parts.splice(index, 1, part);
        setParts([...parts]);
    };
    const deletePart = (index) => {
        parts.splice(index, 1);
        setParts([...parts]);
    };
    const onDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) {
            // dropped outside the list
            return;
        }

        setParts(reorder(
            parts,
            source.index,
            destination.index
        ));
    };

    const partComponents = parts
        .map((part, i) =>
            <PartRowDroppable
                key={part._id}
                index={i}
                part={part}
                updatePart={(part) => updatePart(part, i)}
                deletePart={() => deletePart(i)}
            />);


    const addExistingPart = () => createPart({ _existing: true, _id });
    const addNewPart = () => createPart({ _existing: false, _id });


    const subtotal = parts
        .map(p => p._price || 0)
        .reduce((a, b = 0) => a + b, 0);

    const [shippingCost, setShippingCost] = useState(0);
    const [otherCost, setOtherCost] = useState(0);

    // sales tax is an iterable with first value being either
    // - the currency symbol - represents fixed sales tax
    // - `%` - represents tax to be calculated in percentage
    const [salesTax, setSalesTax] = useState([currencySymbol, 0]);
    const totalBeforeTax = subtotal + shippingCost + otherCost;

    const tax = salesTax[0] === currencySymbol
        ? salesTax[1]
        : totalBeforeTax
            ? (totalBeforeTax * salesTax[1]) / 100
            : 0;
    const total = totalBeforeTax + tax;

    useEffect(() => {
        if (!onChange) {
            return;
        }
        const partsData = parts
            .map(part => part.data)
            .filter(part => part.name)
            .map(part => {
                part.id = part.objectId;
                return part;
            });
        onChange(partsData, subtotal, shippingCost, otherCost, tax, total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parts, shippingCost, otherCost, salesTax]);

    return <>
        <HeaderWrapper>
            <div>
                <span>{parts.length} Parts Added</span>
            </div>

            <div>
                <ButtonWrapper onClick={addExistingPart}>
                    <PlusIcon/> Part from Inventory
                </ButtonWrapper>
                <ButtonWrapper onClick={addNewPart}>
                    <PlusIcon/> Non-existing Part
                </ButtonWrapper>
            </div>
        </HeaderWrapper>

        <Media query={{ maxWidth: 768 }}>
            {matches => matches ? null :
                <PartsHeaderWrapper>
                    <span>#</span>
                    <span>Name</span>
                    <span>Cost</span>
                    <span>Quantity</span>
                    <span>Description</span>
                    <span>Price</span>
                    <span/>
                </PartsHeaderWrapper>
            }
        </Media>

        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="parts-droppable">
                {(provided, snapshot) =>
                    <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        data-dragging={snapshot.isDraggingOver}
                    >
                        {partComponents}
                    </div>
                }

            </Droppable>
        </DragDropContext>

        <PartsCostHeaderWrapper>
            <span>Subtotal</span>
            <span>Shipping Cost</span>
            <span>Other Costs</span>
            <span>Sales Tax</span>
            <span>Total</span>
        </PartsCostHeaderWrapper>

        <PartsCostWrapper>
            <div className="container">
                {currencySymbol} {subtotal}
            </div>
            <div className="container">
                <CostInput
                    placeholder="0"
                    value={shippingCost}
                    onChange={setShippingCost}
                />
            </div>
            <div className="container">
                <CostInput
                    placeholder="0"
                    value={otherCost}
                    onChange={setOtherCost}
                />
            </div>
            <div className="container">
                <TaxInput
                    placeholder="0"
                    value={salesTax[1]}
                    onChange={setSalesTax}
                />
            </div>
            <div className="container">
                {currencySymbol} {total.toFixed(2)}
            </div>
        </PartsCostWrapper>
    </>;
};

export default PartsForm;
